import React, { useState } from "react";
import {
  deleteHistoryEntry,
  loadHistory,
  RoundHistoryEntry,
} from "./activeRound";
import { refreshSharedHistory } from "./caddieGamesStore";
import { deleteRoundForAll, deleteSharedRound } from "./connections";
import LeaderboardModal from "./modals/LeaderboardModal";
import ConfirmModal from "./modals/ConfirmModal";
import Scorecard from "./Scorecard";
import TournamentResults from "./TournamentResults";

interface Props {
  onBack: () => void;
}

// Round history. Tap a row to open the Leaderboard for that round; from
// there a "View scorecard" link expands into the full read-only print
// view. Each row also has a × button → confirm → permanent delete.
type SubMode = "leaderboard" | "scorecard";

export default function History({ onBack }: Props) {
  const [rounds, setRounds] = useState<RoundHistoryEntry[]>(() =>
    loadHistory(),
  );
  const [selected, setSelected] = useState<RoundHistoryEntry | null>(null);
  const [mode, setMode] = useState<SubMode>("leaderboard");
  const [pendingDelete, setPendingDelete] =
    useState<RoundHistoryEntry | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Pull rounds connected players have committed since this screen loaded,
  // without a logout/re-hydrate. Re-reads history from storage after the
  // merge so any newly-shared round appears immediately.
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await refreshSharedHistory();
      setRounds(loadHistory());
    } finally {
      setRefreshing(false);
    }
  };

  const subtitleFor = (r: RoundHistoryEntry): string => {
    const date = new Date(r.endedAt).toLocaleDateString();
    const playerCount = r.players.length;
    return `${date} · ${playerCount} player${playerCount === 1 ? "" : "s"}`;
  };

  const closeAll = () => {
    setSelected(null);
    setMode("leaderboard");
  };

  // Remove from my own history only. Drops my shared copy on the backend too
  // (no-op if I'm the owner) so it doesn't resurrect on the next refresh.
  // The owner and any other participants keep the round in their history.
  const deleteMine = () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    deleteHistoryEntry(id);
    void deleteSharedRound(id);
    setRounds(loadHistory());
    setPendingDelete(null);
  };

  // Owner-only: wipe the round for EVERY participant (their shared copies) as
  // well as my own. The backend re-checks ownership, so this is safe even if
  // the UI gate is ever wrong. Recipients drop it on their next refresh.
  const deleteForEveryone = () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    void deleteRoundForAll(id);
    deleteHistoryEntry(id);
    setRounds(loadHistory());
    setPendingDelete(null);
  };

  const noop = () => {};

  // Tournament rounds get the cross-group results view (My Group | Tournament
  // toggle + a combined card stacking every group) instead of the normal
  // single-group history card. The round's own players/scores/games feed the
  // "My Group" board; the tournament-wide standings come from every group's
  // submitted scores. TournamentResults manages its own leaderboard ↔ scorecard.
  if (selected && selected.tournamentId) {
    return (
      <TournamentResults
        tournamentId={selected.tournamentId}
        course={selected.course}
        games={selected.tournamentGames ?? []}
        title={selected.tournamentName || selected.course.name}
        subtitle={subtitleFor(selected)}
        displayTeeId={selected.displayTeeId}
        localPlayers={selected.players}
        localScores={selected.scores}
        localGames={selected.games}
        tournamentGroupId={selected.tournamentGroupId}
        tournamentGroupName={selected.tournamentGroupName}
        onClose={closeAll}
      />
    );
  }

  if (selected && mode === "scorecard") {
    return (
      <Scorecard
        course={selected.course}
        players={selected.players}
        scores={selected.scores}
        displayTeeId={selected.displayTeeId}
        onPlayersChange={noop}
        onScoresChange={noop}
        onDisplayTeeChange={noop}
        onChangeCourse={() => setMode("leaderboard")}
        onEndRound={noop}
        readOnly
      />
    );
  }

  return (
    <div className="cg-subpage cg-subpage-narrow">
      <div className="cg-subpage-head">
        <button className="cg-btn cg-btn-ghost" onClick={onBack}>
          ← Back
        </button>
        <h2>History</h2>
        <button
          type="button"
          className={`cg-history-refresh${refreshing ? " spinning" : ""}`}
          onClick={handleRefresh}
          disabled={refreshing}
          aria-label="Refresh history"
          title="Refresh"
        >
          ↻
        </button>
      </div>

      {rounds.length === 0 ? (
        <p className="cg-subpage-note">No rounds yet.</p>
      ) : (
        <ul className="cg-history-list">
          {rounds.map((r) => {
            const date = new Date(r.endedAt).toLocaleDateString();
            // Tournament rounds read the tournament name + full field size so
            // they're distinguishable from a normal foursome; otherwise it's
            // the club name + this round's player count.
            const isTournament = !!r.tournamentId;
            const title = isTournament
              ? r.tournamentName || r.course.name
              : r.course.name;
            const playerCount = isTournament
              ? r.tournamentPlayerCount ?? r.players.length
              : r.players.length;
            return (
              <li key={r.id} className="cg-history-row">
                <button
                  type="button"
                  className="cg-history-main"
                  onClick={() => {
                    setSelected(r);
                    setMode("leaderboard");
                  }}
                >
                  <div className="cg-history-name">{title}</div>
                  <div className="cg-history-meta">
                    {date} · {playerCount} player
                    {playerCount === 1 ? "" : "s"}
                  </div>
                </button>
                <button
                  type="button"
                  className="cg-history-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPendingDelete(r);
                  }}
                  aria-label={`Delete round at ${r.course.name}`}
                  title="Delete"
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {selected && mode === "leaderboard" && (
        <LeaderboardModal
          players={selected.players}
          scores={selected.scores}
          totalPar={selected.course.holes.reduce((s, h) => s + h.par, 0)}
          course={selected.course}
          games={selected.games}
          ooslerSelections={selected.ooslerSelections}
          subtitle={subtitleFor(selected)}
          onViewScorecard={() => setMode("scorecard")}
          onClose={closeAll}
        />
      )}

      {/* Rounds I recorded myself carry no sharedOwnerId — those are the ones I
          own, so they additionally offer "Delete for everyone" (which wipes the
          copy from every player it was shared with). On a round someone else
          shared with me, deletion only ever removes my own copy. The backend
          re-checks ownership on the delete-for-all endpoint regardless. */}
      {pendingDelete &&
        (() => {
          const iOwnIt = !pendingDelete.sharedOwnerId;
          return (
            <ConfirmModal
              title="Delete round?"
              message={
                iOwnIt
                  ? `Delete the round at ${pendingDelete.course.name} from your history, or delete it for everyone who played — removing it from their history too. This can't be undone.`
                  : `This removes the round at ${pendingDelete.course.name} from your history only — any other players who were on the card keep it in theirs. This can't be undone.`
              }
              confirmLabel={iOwnIt ? "Delete for me" : "Delete"}
              variant="danger"
              extraLabel={iOwnIt ? "Delete for everyone" : undefined}
              onExtra={iOwnIt ? deleteForEveryone : undefined}
              onConfirm={deleteMine}
              onCancel={() => setPendingDelete(null)}
            />
          );
        })()}
    </div>
  );
}
